import { useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../../../context/ThemeContext'
import { useSyncStatus } from '../../../context/DiscogsContext'
import { useAuth } from '../../../context/AuthContext'
import { useFolders, useDeleteFolder } from '../../../hooks/useFolders'
import { CreateFolderModal } from '../../Folders/CreateFolderModal'
import { FolderNavLink } from '../../Folders/FolderNavLink'
import { IconButton, Modal, Button, ContextMenu } from '../index'
import type { ContextMenuItem } from '../index'
import type { Folder } from '../../../types/folder'
import { useToast } from '../../../context/ToastContext'
import { MAP_ENABLED } from '../../../config/features'
import styles from './Sidebar.module.css'

const discographyLinks = [
  { to: '/releases', label: 'Releases', icon: 'bi-collection' },
  { to: '/artists', label: 'Artists', icon: 'bi-person-badge' },
  { to: '/labels', label: 'Labels', icon: 'bi-tag-fill' },
  { to: '/shuffle', label: 'Shuffle', icon: 'bi-shuffle' },
  { to: '/map', label: 'Map', icon: 'bi-map' },
  { to: '/sellers', label: 'Sellers', icon: 'bi-shop' },
]

const profileLinks = [
  { to: '/collection', label: 'Collection', icon: 'bi-folder' },
  { to: '/wantlist', label: 'Wantlist', icon: 'bi-folder' },
  { to: '/tracking', label: 'Tracking', icon: 'bi-folder' },
]

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { theme, toggleTheme } = useTheme()
  const { syncProgress } = useSyncStatus()
  const { user: authUser, isAdmin, logout: appLogout } = useAuth()
  const { data: foldersData } = useFolders()
  const deleteFolder = useDeleteFolder()
  const { showToast } = useToast()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [menu, setMenu] = useState<{ folder: Folder; x: number; y: number } | null>(null)
  const [editFolder, setEditFolder] = useState<Folder | null>(null)
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const folders = foldersData?.folders || []

  const handleFolderContextMenu = (e: React.MouseEvent, folder: Folder) => {
    e.preventDefault()
    setMenu({ folder, x: e.clientX, y: e.clientY })
  }

  const menuItems: ContextMenuItem[] = menu ? [
    { label: 'Open', icon: 'bi-box-arrow-up-right', onClick: () => navigate(`/folders/${menu.folder.id}`) },
    { label: 'Rename', icon: 'bi-pencil', onClick: () => setEditFolder(menu.folder) },
    { label: 'Delete', icon: 'bi-trash', danger: true, onClick: () => setFolderToDelete(menu.folder) },
  ] : []

  const confirmDelete = async () => {
    if (!folderToDelete) return
    try {
      await deleteFolder.mutateAsync(folderToDelete.id)
      showToast(`Deleted "${folderToDelete.name}"`, 'success')
      if (location.pathname === `/folders/${folderToDelete.id}`) navigate('/releases')
      setFolderToDelete(null)
    } catch {
      showToast('Failed to delete folder', 'error')
    }
  }

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    onClose?.()
  }, [location.pathname])

  return (
    <aside className={`${styles.sidebar}${isOpen ? ' ' + styles.sidebarOpen : ''}`}>
      {/* Brand Header */}
      <div className={styles.header}>
        <NavLink to="/" className={styles.brand}>
          <div className={styles.brandIcon}>
            <span className={styles.brandIconText}>tw</span>
          </div>
          <span className={styles.brandName}><span className={styles.brandTen}>ten</span><span className={styles.brandWords}>words</span></span>
        </NavLink>
        <IconButton
          variant="ghost"
          size="sm"
          icon={`bi bi-${theme === 'dark' ? 'sun' : 'moon'}`}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          onClick={toggleTheme}
        />
      </div>

      {/* Navigation */}
      <nav className={styles.nav} data-tour="sidebar-nav">
        {/* Discography Section */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Discography</div>
          {discographyLinks.filter(link => MAP_ENABLED || link.to !== '/map').map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              data-tour={link.to === '/releases' ? 'nav-releases' : undefined}
              data-mobile-hidden={link.to === '/map' ? '' : undefined}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
            >
              <i className={`bi ${link.icon}`}></i>
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* Profile Section */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Profile</div>
          {profileLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
            >
              <i className={`bi ${link.icon}`}></i>
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* Folders Section */}
        <div className={`${styles.section} ${styles.foldersSection}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionLabel}>Folders</div>
            <IconButton
              variant="ghost"
              size="sm"
              icon="bi bi-plus"
              aria-label="Create folder"
              title="Create folder"
              onClick={() => setShowCreateModal(true)}
            />
          </div>
          <div className={styles.folderList}>
            {folders.length === 0 ? (
              <div className={styles.emptyFolders}>
                No folders yet
              </div>
            ) : (
              folders.map((folder) => (
                <FolderNavLink key={folder.id} folder={folder} onContextMenu={handleFolderContextMenu} />
              ))
            )}
          </div>
        </div>
      </nav>

      {/* Sync Progress */}
      {syncProgress.type && (
        <div className={styles.syncProgress}>
          <i className="bi bi-arrow-repeat"></i>
          <span>
            {syncProgress.type === 'wantlist' ? 'Wantlist' : 'Collection'}
            {' '}{syncProgress.current}/{syncProgress.total}
            {' '}({syncProgress.totalItems.toLocaleString()})
          </span>
        </div>
      )}

      {/* User Section */}
      <div className={styles.userSection}>
        {authUser && (
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {authUser.email.slice(0, 1).toUpperCase()}
            </div>
            <span className={styles.userName}>{authUser.email.split('@')[0]}</span>
            <div className={styles.userActions}>
              {isAdmin && (
                <IconButton variant="ghost" size="sm" icon="bi bi-shield-lock" aria-label="Admin" title="Admin" onClick={() => navigate('/admin')} />
              )}
              <IconButton variant="ghost" size="sm" icon="bi bi-gear" aria-label="Settings" title="Settings" onClick={() => navigate('/settings')} />
              <IconButton variant="ghost" size="sm" icon="bi bi-box-arrow-right" aria-label="Sign out" title="Sign out" onClick={() => { appLogout(); navigate('/login') }} />
            </div>
          </div>
        )}
      </div>

      {/* Folder right-click menu */}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menuItems}
          onClose={() => setMenu(null)}
        />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Folder Modal */}
      <CreateFolderModal
        isOpen={!!editFolder}
        folder={editFolder}
        onClose={() => setEditFolder(null)}
      />

      {/* Delete confirmation */}
      <Modal
        open={!!folderToDelete}
        onClose={() => setFolderToDelete(null)}
        title="Delete folder"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFolderToDelete(null)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleteFolder.isPending}>
              {deleteFolder.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        Delete <strong>{folderToDelete?.name}</strong>? Releases in it won&apos;t be deleted, only the folder.
      </Modal>
    </aside>
  )
}
